import * as React from 'react';
import { Fallback } from 'router';
import { AppContainer, ContentContainer, MainContent, WarningBanner } from './styled';
import { useRecoilState, useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { alertBoxAtom } from 'common/state/ui';
import AlertMessage from 'common/components/AlertMessage';
import { accountDetailsSelector } from 'common/state/account';
import { MoeConfidenceLevelsAtom } from 'common/state/moe';
import { useStudyContext } from 'common/contexts/StudyContext';
import ToastMessage from 'common/components/ToastMessage';
import { useGetWaveReports } from 'common/queries/waveReports';
import { useChartDashboardsContext } from 'common/contexts/ChartsContext';
import { useChartModalContent } from 'common/components/Layout/useChartModalContent';
import { useLocation } from 'react-router-dom';
import SidebarNav from 'common/components/SidebarNav';
import { useRefreshAccountDetails } from 'common/components/Layout/useRefreshAccountDetails';

const CSVExportModal = React.lazy(() => import('common/components/CSVExportModal'));
const DashboardsModal = React.lazy(() => import('common/components/Modal/DashboardsModal'));
const StudySelectionModal = React.lazy(() => import('common/components/StudySelectionModal'));
const FolderCreationModal = React.lazy(() => import('common/components/FolderCreationModal'));
const ReportModal = React.lazy(() => import('modules/settings/components/WaveReports/ReportModal'));

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [csvModalOpen, setCSVModalOpen] = React.useState(false);
  const [alertBox] = useRecoilState(alertBoxAtom);
  const { data: waveReports } = useGetWaveReports();
  const dashboardRef = React.useRef<HTMLDivElement>(null);
  const location = useLocation();

  const accountDetails = useRecoilValueLoadable(accountDetailsSelector);
  const warningBanner = accountDetails.state === 'hasValue' ? accountDetails.contents?.warning_banner : null;
  const setMoeConfidenceLevels = useSetRecoilState(MoeConfidenceLevelsAtom);
  useRefreshAccountDetails();

  const [showCreateFolderModal, setShowCreateFolderModal] = React.useState(false);

  React.useEffect(() => {
    if (accountDetails.state === 'hasValue' && accountDetails.contents?.preferences?.confidence_levels) {
      setMoeConfidenceLevels(accountDetails.contents?.preferences?.confidence_levels);
    }
  }, [accountDetails.state, accountDetails.contents, setMoeConfidenceLevels]);

  const chartModalContent = useChartModalContent();

  const { isSelectionModalOpen } = useStudyContext();
  const { isModalOpen: isChartDashboardModalOpen, setModalOpen: togglelChartDashboardsModal } = useChartDashboardsContext();

  const newWaveReport = React.useMemo(() => {
    return waveReports?.find(({ is_new }) => is_new);
  }, [waveReports]);

  /** To scroll dashboard page up after redirect from dashboards list  **/
  React.useEffect(() => {
    const scrollToTop = () => {
      if (dashboardRef?.current) {
        dashboardRef?.current?.scrollTo(0, 0);
      }
    };
    scrollToTop();
  }, [location]);

  const handleCreateFolderRequest = React.useCallback(() => {
    setShowCreateFolderModal(true);
  }, []);

  return (
    <AppContainer data-has-alert={alertBox?.show}>
      <AlertMessage />
      <SidebarNav onCreateFolder={handleCreateFolderRequest} />
      <ContentContainer>
        {warningBanner?.text?.trim() && <WarningBanner role='alert'>{warningBanner.text}</WarningBanner>}
        {csvModalOpen && (
          <React.Suspense fallback={<Fallback />}>
            <CSVExportModal onClose={() => setCSVModalOpen(false)} open={csvModalOpen} />
          </React.Suspense>
        )}
        <MainContent ref={dashboardRef}>{children}</MainContent>
        {newWaveReport && (
          <React.Suspense fallback={<Fallback />}>
            <ReportModal report={newWaveReport} />
          </React.Suspense>
        )}
        {isSelectionModalOpen && (
          <React.Suspense fallback={<Fallback />}>
            <StudySelectionModal isOpen={isSelectionModalOpen} />
          </React.Suspense>
        )}
        {isChartDashboardModalOpen && (
          <React.Suspense fallback={<Fallback />}>
            <DashboardsModal
              isOpen={isChartDashboardModalOpen}
              toggleDashboardModal={togglelChartDashboardsModal}
              renderContent={chartModalContent}
            />
          </React.Suspense>
        )}
        {showCreateFolderModal && (
          <React.Suspense fallback={<Fallback />}>
            <FolderCreationModal isOpen={showCreateFolderModal} onClose={() => setShowCreateFolderModal(false)} />
          </React.Suspense>
        )}
      </ContentContainer>
      <ToastMessage />
    </AppContainer>
  );
};

export default React.memo(Layout);
